import { useTranslation } from "react-i18next";
import { Checkbox, Grid, Icon } from "semantic-ui-react";

import "components/CustomerModal/components/CustomerDetail/CustomerDetail.scss";
import { COMMUNICATION_CHANNELS, SMS_GATEWAYS } from "models";

import type { Customer, Dealer, DealerLocation } from "models";
import type { FormEvent } from "react";
import type { CheckboxProps } from "semantic-ui-react";

type CustomerDetailProps = {
  customer: Customer;
  isUpdatingCustomer: boolean;
  dealer?: Dealer | null;
  location?: DealerLocation | null;
  onChangePreferredCommunicationChannel: (channel: COMMUNICATION_CHANNELS) => void;
  onChangeCheckbox: (event: FormEvent<HTMLInputElement>, data: CheckboxProps) => void;
};

export const CustomerDetail = ({ customer, dealer, location, isUpdatingCustomer, onChangeCheckbox, onChangePreferredCommunicationChannel }: CustomerDetailProps) => {
  const { t } = useTranslation();

  const isSMSEnabled = !!dealer?.sms_gateway_id && dealer.sms_gateway_id > SMS_GATEWAYS.Disabled;
  const isWhatsappEnabled = !!location?.is_bird_whats_app_webhook_set;

  return (
    <>
      <div className="customer_sys">
        <div className="customer_sys_info">
          <strong>DMS : </strong>
          {customer.dms_nr}
        </div>
        {customer.dealer && (
          <div className="customer_sys_info">
            <strong>{customer.dealer.name}</strong>
          </div>
        )}
        {customer.dealer_location && (
          <div className="customer_sys_info">
            <strong>{customer.dealer_location.name}</strong>
          </div>
        )}
      </div>

      <div className="CustomerDetail">
        <Grid columns={2}>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_company_name", "Company name")}</strong>
              <span>{customer.company}</span>
            </Grid.Column>
            {customer.is_lease_company && (
              <Grid.Column>
                <span className="lease-company-tag">
                  <Icon name="building" />
                  {t("v8_lease_company", "Lease company")}
                </span>
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_location", "Location")}</strong>
              <span>{customer.place}</span>
            </Grid.Column>
            <Grid.Column>
              <strong>{t("v8_street", "Street")}</strong>
              <span>
                {customer.street} {customer.house_nr}
              </span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_postcode", "Postcode")}</strong>
              <span>{customer.postcode}</span>
            </Grid.Column>
            <Grid.Column>
              <strong>{t("v8_business_email", "Business email")}</strong>
              <span>{customer.email_business}</span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_private_email", "Private email")}</strong>
              <span>{customer.email_private}</span>
            </Grid.Column>
            <Grid.Column>
              <strong>{t("v8_business_phone", "Business phone")}</strong>
              <span>{customer.tel_business_nr}</span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_private_phone", "Private phone")}</strong>
              <span>{customer.tel_private_nr}</span>
            </Grid.Column>
            <Grid.Column>
              <strong>{t("v8_business_mobile_phone", "Business mobile phone")}</strong>
              <span>{customer.tel_mobile_business}</span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <strong>{t("v8_private_mobile_phone", "Private mobile phone")}</strong>
              <span>{customer.tel_mobile_private}</span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Checkbox
                name="is_internal"
                toggle
                checked={customer.is_internal}
                onChange={onChangeCheckbox}
                disabled={isUpdatingCustomer}
                label={t("v8_is_internal", "Is internal")}
              />
            </Grid.Column>
            <Grid.Column>
              <Checkbox
                name="block_communication"
                toggle
                checked={customer.block_communication}
                onChange={onChangeCheckbox}
                disabled={isUpdatingCustomer}
                label={t("v8_block_communication", "Block communication")}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ alignItems: "center" }}>
            <Grid.Column width={3}>
              <strong>{t("v8_preferred_channel", "Preferred Channel")}</strong>
            </Grid.Column>

            <Grid.Column>
              <button
                className={`preferred-channel-button ${customer.preferred_communication_channel === COMMUNICATION_CHANNELS.Email ? "active-preferred-channel" : ""}`}
                disabled={isUpdatingCustomer}
                onClick={() => onChangePreferredCommunicationChannel(COMMUNICATION_CHANNELS.Email)}
              >
                <Icon name="mail outline" />
                <span>{t("v8_email", "Email")}</span>
              </button>
              {isSMSEnabled && (
                <button
                  className={`preferred-channel-button ${customer.preferred_communication_channel === COMMUNICATION_CHANNELS.SMS ? "active-preferred-channel" : ""}`}
                  disabled={isUpdatingCustomer}
                  onClick={() => onChangePreferredCommunicationChannel(COMMUNICATION_CHANNELS.SMS)}
                >
                  <Icon name="mobile alternate" />
                  <span>{t("v8_sms", "SMS")}</span>
                </button>
              )}
              {isWhatsappEnabled && (
                <button
                  className={`preferred-channel-button ${customer.preferred_communication_channel === COMMUNICATION_CHANNELS.WhatsApp ? "active-preferred-channel" : ""}`}
                  disabled={isUpdatingCustomer}
                  onClick={() => onChangePreferredCommunicationChannel(COMMUNICATION_CHANNELS.WhatsApp)}
                >
                  <Icon className="whatsapp brand" />
                  <span>WhatsApp</span>
                </button>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </>
  );
};
